import { useEffect, useState } from "react";
import { useGoogleSyncStore } from "../../stores/googleSyncStore";
import { useCalendarStore } from "../../stores/calendarStore";
import { getGoogleConnectUrl } from "../../lib/googleApi";

export function GoogleCalendarSection() {
  const configured = useGoogleSyncStore((s) => s.configured);
  const connected = useGoogleSyncStore((s) => s.connected);
  const lastSyncedAt = useGoogleSyncStore((s) => s.lastSyncedAt);
  const syncing = useGoogleSyncStore((s) => s.syncing);
  const syncError = useGoogleSyncStore((s) => s.error);
  const syncedCalendarIds = useGoogleSyncStore((s) => s.syncedCalendarIds);
  const fetchStatus = useGoogleSyncStore((s) => s.fetchStatus);
  const triggerSync = useGoogleSyncStore((s) => s.triggerSync);
  const disconnect = useGoogleSyncStore((s) => s.disconnect);
  const fetchCalendars = useCalendarStore((s) => s.fetch);
  const calendars = useCalendarStore((s) => s.calendars);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("google") === "connected") {
      fetchStatus();
      fetchCalendars();
      window.history.replaceState({}, "", "/settings");
    }
  }, [fetchStatus, fetchCalendars]);

  if (!configured) return null;

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { url } = await getGoogleConnectUrl();
      window.location.href = url;
    } catch {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm("Disconnect Google Calendar? All synced calendars and events will be removed.")) {
      return;
    }
    await disconnect();
    fetchCalendars();
  };

  const handleSync = async () => {
    await triggerSync();
    fetchCalendars();
  };

  const syncedCalendars = calendars.filter((c) => syncedCalendarIds.has(c.id));

  const formatTime = (iso: string) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="settings-section">
      <p className="settings-eyebrow">Google Calendar</p>
      <p className="settings-desc">
        {connected
          ? "Your Google calendars are synced as read-only."
          : "Import your Google Calendar events (read-only)."}
      </p>

      {connected ? (
        <>
          {syncedCalendars.length > 0 && (
            <div className="form-group">
              <label className="label">Synced calendars</label>
              <div className="google-sync-calendar-list">
                {syncedCalendars.map((cal) => (
                  <div key={cal.id} className="google-sync-calendar-item">
                    <span
                      className="calendar-color-dot"
                      style={{ background: cal.color }}
                    />
                    <span>{cal.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {lastSyncedAt && (
            <p className="muted" style={{ marginBottom: 12 }}>
              Last synced: {formatTime(lastSyncedAt)}
            </p>
          )}

          {syncError && <p className="error-text" style={{ marginBottom: 12 }}>{syncError}</p>}

          <div className="form-actions" style={{ justifyContent: "flex-start", gap: 8 }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? "Syncing..." : "Sync Now"}
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={handleDisconnect}
            >
              Disconnect
            </button>
          </div>
        </>
      ) : (
        <div className="form-actions" style={{ justifyContent: "flex-start" }}>
          <button
            className="btn btn-primary"
            onClick={handleConnect}
            disabled={connecting}
          >
            {connecting ? "Connecting..." : "Connect Google Calendar"}
          </button>
        </div>
      )}
    </div>
  );
}
